import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';
import { formatCurrency } from '../../i18n/i18n';

/**
 * Debug menu — add funds, unlock features, manipulate state.
 * Only accessible via keyboard shortcut (F9).
 */
export function openDebugMenu(parent: HTMLElement, store: GameStore, onClose: () => void): void {
  const prevSpeed = store.getState().speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const dialog = new Dialog({
    title: 'Debug Menu',
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
  });

  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:6px;min-width:320px;max-height:450px;overflow-y:auto;';

  const statusEl = document.createElement('div');
  statusEl.style.cssText = 'font-size:11px;color:var(--color-success);min-height:16px;text-align:center;';

  function showStatus(msg: string) {
    statusEl.textContent = msg;
    setTimeout(() => { statusEl.textContent = ''; }, 2000);
  }

  function addSection(title: string) {
    const el = document.createElement('div');
    el.textContent = title;
    el.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;margin-top:4px;';
    container.appendChild(el);
  }

  function addBtn(label: string, onClick: () => void) {
    const btn = document.createElement('button');
    btn.className = 'btn';
    btn.textContent = label;
    btn.style.cssText = 'font-size:11px;padding:6px 12px;text-align:left;';
    btn.addEventListener('click', onClick);
    container.appendChild(btn);
    return btn;
  }

  // Current state
  const state = store.getState();
  const info = document.createElement('div');
  info.style.cssText = 'font-size:10px;color:var(--color-text-secondary);';
  info.textContent = `Year ${state.company.currentYear}, Week ${state.company.currentWeek} · Funds: ${formatCurrency(state.company.funds)} · Staff: ${state.staff.length} · Level: ${state.company.level}`;
  container.appendChild(info);

  // Funds
  addSection('FUNDS');
  addBtn('+$5,000', () => {
    const s = store.getState();
    (s as { company: { funds: number } }).company.funds += 5000;
    showStatus('+$5,000');
  });
  addBtn('+$50,000', () => {
    const s = store.getState();
    (s as { company: { funds: number } }).company.funds += 50000;
    showStatus('+$50,000');
  });
  addBtn('+$500,000', () => {
    const s = store.getState();
    (s as { company: { funds: number } }).company.funds += 500000;
    showStatus('+$500,000');
  });

  // Staff
  addSection('STAFF');
  addBtn('Max all staff stats (99)', () => {
    const s = store.getState();
    for (const staff of s.staff) {
      for (const stat of Object.keys(staff.stats)) {
        (staff.stats as Record<string, number>)[stat] = 99;
      }
    }
    showStatus('All staff stats set to 99');
  });
  addBtn('Level up all staff', () => {
    const s = store.getState();
    for (const staff of s.staff) {
      (staff as { level: number }).level += 5;
    }
    showStatus('All staff +5 levels');
  });
  addBtn('Full morale + energy', () => {
    const s = store.getState();
    for (const staff of s.staff) {
      (staff as { morale: number; energy: number }).morale = 100;
      (staff as { morale: number; energy: number }).energy = 100;
    }
    showStatus('All staff morale & energy maxed');
  });
  addBtn('Reset training cooldowns', () => {
    const s = store.getState();
    for (const staff of s.staff) {
      (staff as { trainCooldownTicks: number }).trainCooldownTicks = 0;
    }
    showStatus('All cooldowns reset');
  });

  // Company
  addSection('COMPANY');
  addBtn('Upgrade office', () => {
    store.dispatch({ type: 'UPGRADE_OFFICE' });
    showStatus('Office upgraded');
  });
  addBtn('+100 reputation', () => {
    const s = store.getState();
    (s as { company: { reputation: number } }).company.reputation = Math.min(100, s.company.reputation + 100);
    showStatus('Reputation maxed');
  });
  addBtn('+10,000 fans', () => {
    const s = store.getState();
    (s as { company: { fans: number } }).company.fans += 10000;
    showStatus('+10,000 fans');
  });
  addBtn('Company level +1', () => {
    const s = store.getState();
    (s as { company: { level: number } }).company.level += 1;
    showStatus('Company level +1');
  });

  // Projects
  addSection('PROJECTS');
  addBtn('Complete all projects instantly', () => {
    const s = store.getState();
    for (const p of s.projects) {
      if (p.phase !== 'complete') {
        (p as { phase: string }).phase = 'ready';
        (p as { phaseProgress: number }).phaseProgress = 100;
        (p as { quality: number }).quality = 80;
        (p as { bugs: number }).bugs = 0;
      }
    }
    showStatus('All projects ready to ship');
  });
  addBtn('Set all project quality to 100%', () => {
    const s = store.getState();
    for (const p of s.projects) {
      (p as { quality: number }).quality = 100;
      (p as { bugs: number }).bugs = 0;
    }
    showStatus('All projects: 100% quality, 0 bugs');
  });

  // Hiring
  addSection('HIRING');
  addBtn('Instant recruiter (fill pool)', () => {
    const s = store.getState();
    if (s.activeRecruiter) {
      (s.activeRecruiter as { ticksRemaining: number }).ticksRemaining = 1;
      showStatus('Recruiter will deliver next tick');
    } else {
      store.dispatch({ type: 'HIRE_RECRUITER', tier: 'premium' });
      const s2 = store.getState();
      if (s2.activeRecruiter) {
        (s2.activeRecruiter as { ticksRemaining: number }).ticksRemaining = 1;
      }
      showStatus('Premium recruiter hired — delivers next tick');
    }
  });

  // Time
  addSection('TIME');
  addBtn('Skip 1 year', () => {
    const s = store.getState();
    const ticksPerYear = 10 * 7 * 52;
    (s.company as { currentTick: number }).currentTick += ticksPerYear;
    showStatus('Skipped 1 year');
  });

  container.appendChild(statusEl);
  dialog.body.appendChild(container);
  dialog.mount(parent);
}
